// ECRIT A LA MAIN. `tools/gen-icons.mjs` ne connait pas ce dossier et ne doit
// jamais l'ecraser (voir l'entete de `./index.ts`).
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import type { IconProps } from '../icon-props.js';
import { DEFAULT_SIZE } from '../icon-props.js';

/**
 * Fabrique une icone TAKI (les glyphes dessines par le fondateur) a partir de
 * ses traces. Meme esprit que `createRemixIcon` (solid, `currentColor`, taille
 * en rem, `forwardRef`, un module par icone pour le tree-shaking), avec UNE
 * difference : le `viewBox` est un PARAMETRE.
 *
 * Pourquoi : le set Streamline est cadre en `0 0 24 24`, ce qui permet a
 * `createRemixIcon` de le coder en dur. Les icones du fondateur sont exportees
 * depuis Illustrator, chacune avec son propre cadre (`0 0 236.84 235.99`,
 * `0 0 239 69.72`...). Ecrire 24 ici ne rognerait pas l'icone, il la rendrait
 * invisible : le trace tomberait tres loin hors du cadre.
 *
 * `size` reste le COTE DE LA BOITE en px de reference, comme pour toutes les
 * autres icones @taki/ui : le glyphe garde ses proportions et se centre dedans
 * (`preserveAspectRatio` par defaut). Une icone large (le menu, 239 x 69.72)
 * occupe donc la meme boite carree que ses voisines dans une rangee, et reste
 * alignee avec elles.
 *
 * `fill="currentColor"` est pose ici sur la racine ET repete sur chaque trace
 * dans les modules d'icones, comme le fait le set remix : sans lui, le glyphe
 * ne suit pas l'encre du bouton qui le porte (les SVG sources d'Illustrator
 * n'ont aucune couleur declaree).
 */
export function createCustomIcon(displayName: string, viewBox: string, body: ReactNode) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
    { size = DEFAULT_SIZE, ...props },
    ref,
  ) {
    // size est en px de reference (maquette 1920) : rendu en rem pour suivre
    // l'echelle globale (tokens.css, regle racine).
    return (
      <svg
        ref={ref}
        width={`${size / 16}rem`}
        height={`${size / 16}rem`}
        viewBox={viewBox}
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        {...props}
      >
        {body}
      </svg>
    );
  });
  Icon.displayName = displayName;
  // viewBox et corps restent lisibles : `./registre.ts` en tire l'entree du
  // registre, pour que la geometrie ne s'ecrive qu'ici.
  return Object.assign(Icon, { viewBox, corps: body });
}
